/**
 * ReviewsPanel — Presentational panel for reservation reviews (BookingDetailModal)
 *
 * Charter: documentation/cursor/Charters/CodeAuditor.md v1.0.1
 * Phase 5b-1 extracted from BookingDetailModal.jsx; JSX moved verbatim. No logic changes.
 *
 * Purpose:
 *   Render the Reviews expandable panel: loading/error/empty/list of reviews.
 *   Presentational only; parent owns state, effects, and fetch. All data passed as props.
 *
 * Assumptions & Invariants:
 *   - No fetch; no hooks; no new logic. Only props in, JSX out.
 *   - Parent owns reviews state, reviewsLoading, reviewsError, and onClose (setTopPanelOpen(null)).
 *
 * Dependencies: React only
 *
 * Version: 1.0.0
 *
 * Changelog:
 *   2026-01-27 Phase 5b-1 extraction from BookingDetailModal (reviews panel JSX only).
 */

export default function ReviewsPanel({
  theme,
  onClose,
  reviewsLoading,
  reviewsError,
  reviews,
}) {
  return (
    <div style={{
      borderTop: `1px solid ${theme === 'dark' ? 'rgba(255, 255, 255, 0.15)' : 'rgba(200, 195, 188, 0.4)'}`,
      padding: '16px 24px',
      backgroundColor: theme === 'dark'
        ? 'rgba(255, 255, 255, 0.02)'
        : 'rgba(0, 0, 0, 0.01)',
    }}>
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '8px',
      }}>
        <p style={{
          fontSize: '14px',
          fontWeight: 'var(--font-weight-medium)',
          color: theme === 'dark' ? 'var(--color-text-secondary-dark)' : 'var(--color-text-secondary)',
          margin: 0,
        }}>
          Reviews
        </p>
        <button
          onClick={onClose}
          style={{
            border: 'none',
            background: 'transparent',
            cursor: 'pointer',
            fontSize: '12px',
            color: theme === 'dark' ? 'var(--color-text-secondary-dark)' : 'var(--color-text-secondary)',
            padding: '4px 8px',
            borderRadius: '4px',
            transition: 'background-color 0.2s ease',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = theme === 'dark'
              ? 'rgba(255, 255, 255, 0.1)'
              : 'rgba(0, 0, 0, 0.05)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = 'transparent';
          }}
        >
          Close
        </button>
      </div>
      {reviewsLoading ? (
        <p style={{
          fontSize: '14px',
          color: theme === 'dark' ? 'var(--color-text-secondary-dark)' : 'var(--color-text-secondary)',
          margin: 0,
          opacity: 0.7,
        }}>
          Loading reviews...
        </p>
      ) : reviewsError ? (
        <p style={{
          fontSize: '14px',
          color: theme === 'dark' ? '#ff6b6b' : '#d32f2f',
          margin: 0,
          fontWeight: 'var(--font-weight-medium)',
        }}>
          {reviewsError}
        </p>
      ) : reviews && reviews.length > 0 ? (
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '16px',
        }}>
          {reviews.map((review, idx) => (
            <div key={review.id || idx} style={{
              padding: '12px',
              backgroundColor: theme === 'dark'
                ? 'rgba(255, 255, 255, 0.03)'
                : 'rgba(0, 0, 0, 0.02)',
              borderRadius: '6px',
              border: `1px solid ${theme === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.08)'}`,
            }}>
              {/* Review Header: Rating + Date */}
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '8px',
              }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                }}>
                  {review.rating && (
                    <span style={{
                      fontSize: '14px',
                      fontWeight: 'var(--font-weight-medium)',
                      color: theme === 'dark' ? '#FBBF24' : '#D97706',
                    }}>
                      {'★'.repeat(Math.min(review.rating, 10))} {review.rating}/10
                    </span>
                  )}
                  {review.title && (
                    <span style={{
                      fontSize: '14px',
                      fontWeight: 'var(--font-weight-medium)',
                      color: theme === 'dark' ? 'var(--color-text-primary-dark)' : 'var(--color-text-primary)',
                    }}>
                      {review.title}
                    </span>
                  )}
                </div>
                {review.submitted_at && (
                  <span style={{
                    fontSize: '12px',
                    color: theme === 'dark' ? 'var(--color-text-secondary-dark)' : 'var(--color-text-secondary)',
                    opacity: 0.7,
                  }}>
                    {new Date(review.submitted_at).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'short',
                      day: 'numeric',
                    })}
                  </span>
                )}
              </div>

              {/* Review Text */}
              {review.public_review && (
                <p style={{
                  fontSize: '14px',
                  color: theme === 'dark' ? 'var(--color-text-primary-dark)' : 'var(--color-text-primary)',
                  margin: 0,
                  lineHeight: '1.5',
                  whiteSpace: 'pre-wrap',
                }}>
                  {review.public_review}
                </p>
              )}

              {/* Review Metadata */}
              <div style={{
                display: 'flex',
                gap: '12px',
                marginTop: '8px',
                fontSize: '12px',
                color: theme === 'dark' ? 'var(--color-text-secondary-dark)' : 'var(--color-text-secondary)',
                opacity: 0.7,
              }}>
                {review.type && (
                  <span>Type: {review.type}</span>
                )}
                {review.status && (
                  <span>Status: {review.status}</span>
                )}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <p style={{
          fontSize: '14px',
          color: theme === 'dark' ? 'var(--color-text-secondary-dark)' : 'var(--color-text-secondary)',
          margin: 0,
          opacity: 0.7,
        }}>
          No reviews are available for this reservation.
        </p>
      )}
    </div>
  );
}
